/** 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR) — tek kaynak Core. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { readNativeAuthRequest } from './native-auth-request.utils';
import './NativeAuthConsent.css';

export { readNativeAuthRequest } from './native-auth-request.utils';

export function useNativeAuthRequestState() {
  const [requestId, setRequestId] = useState(() => typeof window === 'undefined' ? null : readNativeAuthRequest(window.location.hash));
  useEffect(() => {
    const capture = (event?: Event) => {
      // replaceState scrubs the fragment without an event. Keep that in-memory
      // request, but a new fragment or a history traversal replaces it.
      if (window.location.hash || event?.type === 'popstate') setRequestId(readNativeAuthRequest(window.location.hash));
    };
    capture();
    window.addEventListener('hashchange', capture);
    window.addEventListener('popstate', capture);
    return () => {
      window.removeEventListener('hashchange', capture);
      window.removeEventListener('popstate', capture);
    };
  }, []);
  useEffect(() => {
    const hash = window.location.hash;
    if (new URLSearchParams(hash.slice(1)).has('native_request') && readNativeAuthRequest(hash) === requestId)
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
  }, [requestId]);
  const clear = useCallback(() => setRequestId(null), []);
  return { requestId, clear };
}

export function useNativeAuthRequest(): string | null {
  return useNativeAuthRequestState().requestId;
}

const copy: Record<string, [string, string, string, string]> = {
  tr: ['Mobil uygulamaya devam et', 'Bu hesapla QubePro mobil uygulamasında oturum açılacak.', 'Vazgeç', 'Giriş tamamlanamadı. Mobil uygulamadan yeniden başlatın.'],
  en: ['Continue to the mobile app', 'Sign in to QubePro mobile with this account.', 'Cancel', 'Sign-in failed. Start again from the mobile app.'],
  ar: ['المتابعة إلى تطبيق الهاتف', 'تسجيل الدخول إلى QubePro بهذا الحساب.', 'إلغاء', 'تعذر تسجيل الدخول. ابدأ من التطبيق مجدداً.'],
  de: ['Zur mobilen App', 'Mit diesem Konto bei QubePro Mobile anmelden.', 'Abbrechen', 'Anmeldung fehlgeschlagen. In der App neu starten.'],
  fr: ['Continuer sur mobile', 'Se connecter à QubePro mobile avec ce compte.', 'Annuler', 'Connexion impossible. Recommencez depuis l’application.'],
  es: ['Continuar en la app móvil', 'Iniciar sesión en QubePro móvil con esta cuenta.', 'Cancelar', 'No se pudo iniciar sesión. Reinicia desde la app.'],
  it: ['Continua nell’app mobile', 'Accedi a QubePro mobile con questo account.', 'Annulla', 'Accesso non riuscito. Riprova dall’app.'],
  ru: ['Продолжить в приложении', 'Войти в QubePro с этой учётной записью.', 'Отмена', 'Не удалось войти. Начните заново в приложении.'],
  zh: ['继续前往移动应用', '使用此帐户登录 QubePro 移动应用。', '取消', '登录失败。请从移动应用重新开始。'],
  ja: ['モバイルアプリに進む', 'このアカウントで QubePro にログインします。', 'キャンセル', 'ログインできませんでした。アプリからやり直してください。'],
};

interface NativeAuthConsentProps {
  requestId: string; language: string; account?: string; authorize: (requestId: string) => Promise<string>;
  onCancel?: () => void;
}

export function NativeAuthConsent(props: NativeAuthConsentProps) {
  // Failure/cancellation/busy state belongs to one attempt, including when the
  // parent stays mounted during a second system-browser navigation.
  return <NativeAuthConsentAttempt key={props.requestId} {...props} />;
}

function NativeAuthConsentAttempt({ requestId, language, account, authorize, onCancel }: NativeAuthConsentProps) {
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const labels = copy[language] || copy.en;
  const continueLogin = async () => {
    if (busy || failed || cancelled) return;
    setBusy(true);
    try {
      const value = await authorize(requestId);
      if (!active.current || (window.location.hash && readNativeAuthRequest(window.location.hash) !== requestId)) return;
      const target = new URL(value);
      if (target.protocol !== 'https:' || target.username || target.password) throw new Error();
      window.location.assign(value);
    } catch { if (active.current) setFailed(true); }
    finally { if (active.current) setBusy(false); }
  };
  return <div className="native-auth-consent" aria-busy={busy}>
    <p>{labels[1]}</p>
    {account && <p>{account}</p>}
    {(failed || cancelled) ? <p role="status">{failed ? labels[3] : labels[2]}</p> : <>
      <button type="button" className="native-auth-consent__primary" disabled={busy} onClick={() => { void continueLogin(); }}>{labels[0]}</button>
      <button type="button" className="native-auth-consent__secondary" disabled={busy} onClick={() => { setCancelled(true); onCancel?.(); }}>{labels[2]}</button>
    </>}
  </div>;
}
